'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  FieldDefinition,
  FormDefinition,
  SectionDefinition,
} from '@akademi/form-designer/schema';
import { Button, Input, SelectField } from '@akademi/ui';
import { useBlockPart } from '@akademi/site-builder-runtime';

/**
 * Portal-side renderer for a designer-authored {@code FormDefinition}.
 *
 * <p>The admin has its own renderer in {@code @akademi/form-designer}, built on
 * antd through {@code @akademi/base}. The portal ships neither, so this is a
 * second implementation of the same contract rather than a re-export: only the
 * TYPES are shared (a type-only import, erased at build), which is what keeps
 * antd out of the portal bundle.
 *
 * <p>Field kinds map to the portal's own primitives where one exists
 * ({@code Input}, {@code SelectField}) and to native inputs otherwise — a range
 * input for sliders, {@code <input type="date|time|datetime-local|color">} for
 * the temporal and colour kinds. Every kind in the designer's palette renders;
 * none is silently dropped, because a field the operator placed and the
 * applicant never sees is a submission with a hole in it.
 *
 * <p>Validation mirrors the definition's own rules (required, min/max,
 * minLength/maxLength, pattern) and runs on submit, then live per-field once a
 * field has been marked invalid — the usual "don't yell while I'm still
 * typing" behaviour.
 */
export interface DynamicFormRendererProps {
  definition: FormDefinition;
  /** Previously saved answers, for an editable form being re-opened. Merged
   *  over the definition's own defaults. */
  initialValues?: Record<string, unknown>;
  /**
   * Renders a decoy input under this key and submits it with the rest. Left
   * empty by anyone who can see the page — it is hidden from sight and from
   * assistive tech — so a value in it marks an automated submission. The
   * server drops the key before storing.
   */
  honeypotName?: string;
  /** Receives the collected values keyed by field name. */
  onSubmit: (values: Record<string, unknown>) => Promise<void> | void;
  submitting?: boolean;
  submitLabel: string;
  /** Copy for the generated validation messages. */
  labels: ValidationLabels;
}

export interface ValidationLabels {
  required: string;
  tooShort: (n: number) => string;
  tooLong: (n: number) => string;
  tooSmall: (n: number) => string;
  tooLarge: (n: number) => string;
  pattern: string;
}

type Values = Record<string, unknown>;
type Errors = Record<string, string | undefined>;

export function DynamicFormRenderer({
  definition,
  initialValues,
  honeypotName,
  onSubmit,
  submitting = false,
  submitLabel,
  labels,
}: DynamicFormRendererProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const fields = useMemo(() => collectFields(definition), [definition]);
  const [values, setValues] = useState<Values>(() => ({
    ...defaultValues(fields),
    ...(initialValues ?? {}),
  }));
  const [errors, setErrors] = useState<Errors>({});
  /** Only validate-on-change after the first failed submit. */
  const [live, setLive] = useState(false);
  const [honeypot, setHoneypot] = useState('');

  function setValue(field: FieldDefinition, next: unknown) {
    setValues((prev) => ({ ...prev, [field.name]: next }));
    if (live) setErrors((prev) => ({ ...prev, [field.name]: validateField(field, next, labels) }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const found: Errors = {};
    for (const field of fields) {
      const message = validateField(field, values[field.name], labels);
      if (message) found[field.name] = message;
    }
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setLive(true);
      return;
    }
    void onSubmit(honeypotName ? { ...values, [honeypotName]: honeypot } : values);
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <style>{FORM_CSS}</style>
      {definition.sections.map((section) => (
        <FormSection
          key={section.id}
          section={section}
          values={values}
          errors={errors}
          onChange={setValue}
        />
      ))}

      {honeypotName ? (
        // Off-screen rather than display:none — some bots skip inputs that are
        // not rendered at all. aria-hidden + tabIndex keep it away from
        // keyboard and screen-reader users, and autoComplete="off" stops a
        // browser helpfully filling it in on a real person's behalf.
        <div style={honeypotWrapStyle} aria-hidden="true">
          <label htmlFor={honeypotName}>{t('form.honeypot.label')}</label>
          <input
            id={honeypotName}
            name={honeypotName}
            type="text"
            value={honeypot}
            tabIndex={-1}
            autoComplete="off"
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>
      ) : null}

      <div style={submitRowStyle}>
        <Button {...part('submit')} type="submit" variant="brand" size="lg" disabled={submitting}>
          {submitting ? '…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}

/** Field name → a safe `data-<ns>-part` token: `field-<slug>`. */
function fieldPart(name: string): string {
  return `field-${
    name
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'x'
  }`;
}

// ---------------------------------------------------------------------------
// Section + field layout
// ---------------------------------------------------------------------------

function FormSection({
  section,
  values,
  errors,
  onChange,
}: {
  section: SectionDefinition;
  values: Values;
  errors: Errors;
  onChange: (field: FieldDefinition, next: unknown) => void;
}): ReactNode {
  const columns = clamp(Math.trunc(Number(section.columns)) || 1, 1, 4);
  const visible = section.fields.filter((f) => f.kind !== 'hidden');
  if (visible.length === 0) return null;

  return (
    <section style={sectionStyle}>
      {section.title ? <h3 style={sectionTitleStyle}>{section.title}</h3> : null}
      {section.description ? <p style={sectionDescStyle}>{section.description}</p> : null}

      <div
        className="akd-form-grid"
        style={{ ...gridStyle, ['--akd-form-cols' as string]: columns }}
      >
        {visible.map((field) => (
          <FormField
            key={field.name}
            field={field}
            columns={columns}
            value={values[field.name]}
            error={errors[field.name]}
            onChange={(next) => onChange(field, next)}
          />
        ))}
      </div>
    </section>
  );
}

function FormField({
  field,
  columns,
  value,
  error,
  onChange,
}: {
  field: FieldDefinition;
  columns: number;
  value: unknown;
  error?: string;
  onChange: (next: unknown) => void;
}): ReactNode {
  const part = useBlockPart();
  // The designer sizes fields on a 12-column grid; a section renders 1–4
  // columns, so a span is converted into "how many of THIS section's columns".
  const span = clamp(Math.round(((field.colSpan ?? 12) / 12) * columns) || 1, 1, columns);

  return (
    <div {...part(fieldPart(field.name))} style={{ gridColumn: `span ${span}` }}>
      <label style={labelStyle}>
        {field.label}
        {field.required ? <span style={requiredMarkStyle}>*</span> : null}
      </label>
      <FieldControl field={field} value={value} error={Boolean(error)} onChange={onChange} />
      {field.help && !error && !INLINE_HELP_KINDS.has(field.kind) ? (
        <span style={helpStyle}>{field.help}</span>
      ) : null}
      {error ? <span style={errorStyle}>{error}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

/**
 * Kinds whose control already prints `help` next to the box — a tickbox needs
 * its consent sentence beside it, not floating underneath. The field wrapper
 * skips these so the text isn't rendered twice.
 */
const INLINE_HELP_KINDS = new Set<FieldDefinition['kind']>(['switch', 'checkbox']);

function FieldControl({
  field,
  value,
  error,
  onChange,
}: {
  field: FieldDefinition;
  value: unknown;
  error: boolean;
  onChange: (next: unknown) => void;
}): ReactNode {
  const invalid = error;

  switch (field.kind) {
    case 'textarea':
      return (
        <textarea
          value={asString(value)}
          rows={field.rows ?? 4}
          placeholder={field.placeholder}
          disabled={field.disabled}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...controlStyle(invalid), resize: 'vertical', padding: '10px 12px' }}
        />
      );

    case 'number':
      return (
        <Input
          type="number"
          value={asString(value)}
          placeholder={field.placeholder}
          disabled={field.disabled}
          min={field.min}
          max={field.max}
          step={field.step}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          style={controlStyle(invalid)}
        />
      );

    case 'password':
      return (
        <Input
          type="password"
          value={asString(value)}
          placeholder={field.placeholder}
          disabled={field.disabled}
          onChange={(e) => onChange(e.target.value)}
          style={controlStyle(invalid)}
        />
      );

    case 'select':
      return (
        <SelectField
          value={asString(value) || undefined}
          options={field.options.map((o) => ({ value: String(o.value), label: o.label }))}
          onChange={(v) => onChange(v)}
          placeholder={field.placeholder}
          ariaLabel={field.label}
          fullWidth
          style={controlStyle(invalid)}
        />
      );

    case 'multi-select':
    case 'checkbox-group':
      return (
        <CheckableGroup
          options={field.options}
          values={asArray(value)}
          type="checkbox"
          layout={field.kind === 'checkbox-group' ? (field.layout ?? 'vertical') : 'vertical'}
          disabled={field.disabled}
          onChange={onChange}
        />
      );

    case 'radio-group':
    case 'segmented':
      return (
        <CheckableGroup
          options={field.options}
          values={value === undefined || value === null ? [] : [String(value)]}
          type="radio"
          layout={field.kind === 'segmented' ? 'horizontal' : 'vertical'}
          disabled={field.disabled}
          onChange={(next) => onChange((next as string[])[0] ?? '')}
        />
      );

    case 'switch':
    case 'checkbox':
      return (
        <label style={inlineCheckStyle}>
          <input
            type="checkbox"
            checked={value === true}
            disabled={field.disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span style={{ fontSize: 'var(--font-size-sm, 13px)' }}>{field.help ?? ''}</span>
        </label>
      );

    case 'slider':
      return (
        <RangeControl
          value={toNumber(value, field.min ?? 0)}
          min={field.min ?? 0}
          max={field.max ?? 100}
          step={field.step ?? 1}
          disabled={field.disabled}
          onChange={onChange}
        />
      );

    // Two handles would need a custom track; a pair of bounded numbers is
    // honest about the value it produces and needs no drag maths.
    case 'slider-range': {
      const pair = asArray(value);
      const lo = toNumber(pair[0], field.min ?? 0);
      const hi = toNumber(pair[1], field.max ?? 100);
      return (
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            type="number"
            value={String(lo)}
            min={field.min}
            max={field.max}
            step={field.step}
            disabled={field.disabled}
            onChange={(e) => onChange([Number(e.target.value), hi])}
            style={controlStyle(invalid)}
          />
          <Input
            type="number"
            value={String(hi)}
            min={field.min}
            max={field.max}
            step={field.step}
            disabled={field.disabled}
            onChange={(e) => onChange([lo, Number(e.target.value)])}
            style={controlStyle(invalid)}
          />
        </div>
      );
    }

    case 'rate':
      return (
        <StarRating
          value={toNumber(value, 0)}
          count={field.count ?? 5}
          disabled={field.disabled}
          onChange={onChange}
        />
      );

    case 'date':
    case 'time':
    case 'datetime':
      return (
        <Input
          type={field.kind === 'datetime' ? 'datetime-local' : field.kind}
          value={asString(value)}
          disabled={field.disabled}
          onChange={(e) => onChange(e.target.value)}
          style={controlStyle(invalid)}
        />
      );

    case 'date-range': {
      const pair = asArray(value);
      return (
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            type="date"
            value={asString(pair[0])}
            disabled={field.disabled}
            onChange={(e) => onChange([e.target.value, asString(pair[1])])}
            style={controlStyle(invalid)}
          />
          <Input
            type="date"
            value={asString(pair[1])}
            disabled={field.disabled}
            onChange={(e) => onChange([asString(pair[0]), e.target.value])}
            style={controlStyle(invalid)}
          />
        </div>
      );
    }

    case 'color':
      return (
        <input
          type="color"
          value={asString(value) || '#000000'}
          disabled={field.disabled}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...controlStyle(invalid), padding: 4, height: 42, cursor: 'pointer' }}
        />
      );

    case 'file-upload':
      // Names only. Real uploads are blocked on the backend, not here: the
      // central endpoint (`POST /v1/storage/upload/{type}`, StorageWs) is
      // IS_LOGGED_IN and its type list has no form-attachment kind, so a
      // public form could never upload through it and a private one has
      // nowhere to put the bytes. Needs a `form-attachment` type plus its
      // StorageUploadHandler — and a decision on who may read a submitted
      // file back — before this can be more than a filename.
      return (
        <input
          type="file"
          accept={field.accept}
          multiple={field.multiple}
          disabled={field.disabled}
          onChange={(e) => onChange(Array.from(e.target.files ?? []).map((f) => f.name))}
          style={{ ...controlStyle(invalid), padding: 8 }}
        />
      );

    // The picker half of a booking; turning a slot into a calendar event is
    // the admin renderer's `onResolveBookings` hook, which has no portal
    // counterpart yet.
    case 'booking':
      return (
        <Input
          type="datetime-local"
          value={asString(value)}
          disabled={field.disabled}
          min={field.minDate ? field.minDate.slice(0, 16) : undefined}
          max={field.maxDate ? field.maxDate.slice(0, 16) : undefined}
          onChange={(e) => onChange(e.target.value)}
          style={controlStyle(invalid)}
        />
      );

    // Hierarchical pickers: the definition carries opaque tree/option data the
    // designer never normalised, so a flat text entry is the truthful
    // fallback until the shapes are pinned down.
    case 'tree-select':
    case 'cascader':
    case 'text':
    default:
      return (
        <Input
          value={asString(value)}
          placeholder={'placeholder' in field ? field.placeholder : undefined}
          disabled={field.disabled}
          onChange={(e) => onChange(e.target.value)}
          style={controlStyle(invalid)}
        />
      );
  }
}

function CheckableGroup({
  options,
  values,
  type,
  layout,
  disabled,
  onChange,
}: {
  options: Array<{ label: string; value: string | number }>;
  values: unknown[];
  type: 'checkbox' | 'radio';
  layout: 'horizontal' | 'vertical';
  disabled?: boolean;
  onChange: (next: unknown) => void;
}): ReactNode {
  const selected = values.map(String);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: layout === 'horizontal' ? 'row' : 'column',
        flexWrap: 'wrap',
        gap: layout === 'horizontal' ? 14 : 8,
        paddingTop: 4,
      }}
    >
      {options.map((option) => {
        const raw = String(option.value);
        const checked = selected.includes(raw);
        return (
          <label key={raw} style={inlineCheckStyle}>
            <input
              type={type}
              checked={checked}
              disabled={disabled}
              onChange={() => {
                if (type === 'radio') return onChange([raw]);
                onChange(checked ? selected.filter((v) => v !== raw) : [...selected, raw]);
              }}
            />
            <span style={{ fontSize: 'var(--font-size-sm, 13px)' }}>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function RangeControl({
  value,
  min,
  max,
  step,
  disabled,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  onChange: (next: number) => void;
}): ReactNode {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ flex: 1 }}
      />
      <span style={{ minWidth: 40, textAlign: 'right', fontSize: 'var(--font-size-sm, 13px)' }}>
        {value}
      </span>
    </div>
  );
}

function StarRating({
  value,
  count,
  disabled,
  onChange,
}: {
  value: number;
  count: number;
  disabled?: boolean;
  onChange: (next: number) => void;
}): ReactNode {
  return (
    <div style={{ display: 'flex', gap: 4, paddingTop: 4 }}>
      {Array.from({ length: clamp(count, 1, 10) }).map((_, i) => {
        const score = i + 1;
        return (
          <button
            key={score}
            type="button"
            disabled={disabled}
            aria-label={String(score)}
            aria-pressed={value >= score}
            onClick={() => onChange(value === score ? 0 : score)}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: disabled ? 'default' : 'pointer',
              fontSize: 22,
              lineHeight: 1,
              padding: 0,
              color:
                value >= score
                  ? 'var(--color-amber-500, #f59e0b)'
                  : 'var(--color-border-subtle, #d4d4d8)',
            }}
          >
            ★
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Values + validation
// ---------------------------------------------------------------------------

function collectFields(definition: FormDefinition): FieldDefinition[] {
  return definition.sections.flatMap((s) => (Array.isArray(s.fields) ? s.fields : []));
}

/** Hidden fields keep their default: they are carried into the submission
 *  without ever being rendered. */
function defaultValues(fields: FieldDefinition[]): Values {
  const out: Values = {};
  for (const field of fields) {
    if (field.defaultValue !== undefined) out[field.name] = field.defaultValue;
    else if (field.kind === 'switch' || field.kind === 'checkbox') out[field.name] = false;
    else if (field.kind === 'multi-select' || field.kind === 'checkbox-group') out[field.name] = [];
  }
  return out;
}

function validateField(
  field: FieldDefinition,
  value: unknown,
  labels: ValidationLabels,
): string | undefined {
  const rules = field.rules ?? {};

  if (field.required && isEmpty(value)) return labels.required;
  if (isEmpty(value)) return undefined;

  if (typeof value === 'string') {
    if (rules.minLength !== undefined && value.trim().length < rules.minLength) {
      return rules.message ?? labels.tooShort(rules.minLength);
    }
    if (rules.maxLength !== undefined && value.trim().length > rules.maxLength) {
      return rules.message ?? labels.tooLong(rules.maxLength);
    }
    if (rules.pattern) {
      let ok = true;
      try {
        ok = new RegExp(rules.pattern).test(value);
      } catch {
        // An unparseable pattern is an authoring bug, not an applicant's —
        // never block a submission on it.
        ok = true;
      }
      if (!ok) return rules.message ?? labels.pattern;
    }
  }

  const numeric = typeof value === 'number' ? value : undefined;
  if (numeric !== undefined) {
    if (rules.min !== undefined && numeric < rules.min) {
      return rules.message ?? labels.tooSmall(rules.min);
    }
    if (rules.max !== undefined && numeric > rules.max) {
      return rules.message ?? labels.tooLarge(rules.max);
    }
  }

  return undefined;
}

function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (value === false) return true;
  return false;
}

function asString(value: unknown): string {
  if (value === undefined || value === null) return '';
  return String(value);
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toNumber(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

/* A section's column count is the operator's desktop choice; below the phone
   breakpoint every field takes the full width — a two-up row of date pickers
   at 360px is unusable, and `colSpan` can't express that on its own. */
const FORM_CSS = `
@media(max-width:640px){
  .akd-form-grid{grid-template-columns:minmax(0,1fr)!important}
  .akd-form-grid > *{grid-column:span 1!important}
}
`;

const sectionStyle: CSSProperties = {
  marginBottom: 24,
};

const sectionTitleStyle: CSSProperties = {
  margin: '0 0 4px',
  fontSize: 'var(--font-size-lg, 17px)',
  fontWeight: 700,
  color: 'var(--color-text-primary, #111827)',
};

const sectionDescStyle: CSSProperties = {
  margin: '0 0 12px',
  fontSize: 'var(--font-size-sm, 13px)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gap: 14,
  gridTemplateColumns: 'repeat(var(--akd-form-cols, 1), minmax(0, 1fr))',
};

const labelStyle: CSSProperties = {
  display: 'block',
  marginBottom: 6,
  fontSize: 'var(--font-size-sm, 13px)',
  fontWeight: 600,
  color: 'var(--color-text-primary, #111827)',
};

const requiredMarkStyle: CSSProperties = {
  marginLeft: 4,
  color: 'var(--color-danger-500, #ef4444)',
};

const helpStyle: CSSProperties = {
  display: 'block',
  marginTop: 4,
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const errorStyle: CSSProperties = {
  display: 'block',
  marginTop: 4,
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-danger-500, #ef4444)',
};

const inlineCheckStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  cursor: 'pointer',
};

const honeypotWrapStyle: CSSProperties = {
  position: 'absolute',
  left: '-9999px',
  width: 1,
  height: 1,
  overflow: 'hidden',
};

const submitRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: 4,
};

function controlStyle(invalid: boolean): CSSProperties {
  return {
    width: '100%',
    borderRadius: 'var(--radius-md, 8px)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: invalid
      ? 'var(--color-danger-500, #ef4444)'
      : 'var(--color-border-subtle, #e5e7eb)',
    background: 'var(--color-surface-card, #fff)',
    color: 'var(--color-text-primary, #111827)',
    fontFamily: 'inherit',
    fontSize: 'var(--font-size-sm, 14px)',
    boxSizing: 'border-box',
  };
}
